/**
 * CheckUp Store
 *
 * Local state for CheckUp flow with Zustand.
 */

import { create } from 'zustand';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface CheckUpResponseData {
  questionId: string;
  answer: string | number | string[];
  answeredAt: Date;
}

export interface CheckUpState {
  // Current checkup
  currentCheckUpId: string | null;
  currentStep: number;
  totalSteps: number;

  // Responses
  responses: Map<string, CheckUpResponseData>;

  // Navigation
  canGoBack: boolean;
  canGoNext: boolean;
  isSubmitting: boolean;

  // Actions
  startCheckUp: (checkUpId: string, totalSteps: number) => void;
  setResponse: (questionId: string, answer: string | number | string[]) => void;
  getResponse: (questionId: string) => CheckUpResponseData | undefined;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setSubmitting: (submitting: boolean) => void;
  reset: () => void;

  // Computed
  getProgress: () => number;
  getAllResponses: () => CheckUpResponseData[];
}

// ═══════════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════════

const initialState = {
  currentCheckUpId: null,
  currentStep: 0,
  totalSteps: 0,
  responses: new Map<string, CheckUpResponseData>(),
  canGoBack: false,
  canGoNext: false,
  isSubmitting: false,
};

export const useCheckUpStore = create<CheckUpState>((set, get) => ({
  ...initialState,

  startCheckUp: (checkUpId, totalSteps) => {
    set({
      currentCheckUpId: checkUpId,
      currentStep: 0,
      totalSteps,
      responses: new Map(),
      canGoBack: false,
      canGoNext: false,
      isSubmitting: false,
    });
  },

  setResponse: (questionId, answer) => {
    const responses = new Map(get().responses);
    responses.set(questionId, {
      questionId,
      answer,
      answeredAt: new Date(),
    });

    const currentStep = get().currentStep;

    set({
      responses,
      canGoNext: true,
      // Allow going next if not at last step
      canGoBack: currentStep > 0,
    });
  },

  getResponse: (questionId) => {
    return get().responses.get(questionId);
  },

  goToStep: (step) => {
    const totalSteps = get().totalSteps;
    const clampedStep = Math.max(0, Math.min(step, totalSteps - 1));

    set({
      currentStep: clampedStep,
      canGoBack: clampedStep > 0,
    });
  },

  nextStep: () => {
    const { currentStep, totalSteps } = get();
    if (currentStep < totalSteps - 1) {
      set({
        currentStep: currentStep + 1,
        canGoBack: true,
        canGoNext: false, // Reset until new answer
      });
    }
  },

  prevStep: () => {
    const { currentStep } = get();
    if (currentStep > 0) {
      set({
        currentStep: currentStep - 1,
        canGoBack: currentStep - 1 > 0,
        canGoNext: true, // Previous step has answer
      });
    }
  },

  setSubmitting: (submitting) => {
    set({ isSubmitting: submitting });
  },

  reset: () => {
    set(initialState);
  },

  getProgress: () => {
    const { currentStep, totalSteps } = get();
    if (totalSteps === 0) return 0;
    return Math.round((currentStep / totalSteps) * 100);
  },

  getAllResponses: () => {
    return Array.from(get().responses.values());
  },
}));

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useCheckUpFlow() {
  return useCheckUpStore((state) => ({
    checkUpId: state.currentCheckUpId,
    currentStep: state.currentStep,
    totalSteps: state.totalSteps,
    progress: state.getProgress(),
    canGoBack: state.canGoBack,
    canGoNext: state.canGoNext,
    isSubmitting: state.isSubmitting,
    start: state.startCheckUp,
    next: state.nextStep,
    prev: state.prevStep,
    goTo: state.goToStep,
    reset: state.reset,
  }));
}

export function useCheckUpResponse(questionId: string) {
  return useCheckUpStore((state) => ({
    response: state.getResponse(questionId),
    setResponse: (answer: string | number | string[]) => state.setResponse(questionId, answer),
  }));
}
